import Phaser from "phaser";
import { store } from "../state/StateStore";
import { XRewardSystem } from "../systems/XRewardSystem";
import { StorageService } from "../services/StorageService";

export class ResultScene extends Phaser.Scene {
  private xRewardSystem!: XRewardSystem;

  constructor() {
    super({ key: "ResultScene" });
  }

  create(): void {
    const { width, height } = this.cameras.main;
    const centerX = width / 2;
    const state = store.get();

    this.xRewardSystem = new XRewardSystem();

    // Background
    this.cameras.main.setBackgroundColor("#0f0f23");

    // Game Over title
    this.add
      .text(centerX, height * 0.15, "Game Over", {
        fontSize: "38px",
        color: "#e74c3c",
        fontFamily: "Arial, sans-serif",
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    // Score
    this.add
      .text(centerX, height * 0.28, `Score`, {
        fontSize: "20px",
        color: "#aaaaaa",
        fontFamily: "Arial, sans-serif",
      })
      .setOrigin(0.5);

    const scoreText = this.add
      .text(centerX, height * 0.35, `${state.score}`, {
        fontSize: "52px",
        color: "#ffffff",
        fontFamily: "Arial, sans-serif",
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    // Animate score
    this.tweens.add({
      targets: scoreText,
      scaleX: 1.1,
      scaleY: 1.1,
      duration: 300,
      yoyo: true,
      ease: "Bounce",
    });

    // High score
    const isNewRecord = state.score >= state.highScore && state.score > 0;
    const highScoreColor = isNewRecord ? "#ffdd00" : "#888888";
    const highScorePrefix = isNewRecord ? "NEW! " : "";

    this.add
      .text(
        centerX,
        height * 0.44,
        `${highScorePrefix}High Score: ${state.highScore}`,
        {
          fontSize: "20px",
          color: highScoreColor,
          fontFamily: "Arial, sans-serif",
          fontStyle: isNewRecord ? "bold" : "normal",
        }
      )
      .setOrigin(0.5);

    // Stage reached
    this.add
      .text(centerX, height * 0.51, `Stage ${state.stage} reached`, {
        fontSize: "18px",
        color: "#aaaaaa",
        fontFamily: "Arial, sans-serif",
      })
      .setOrigin(0.5);

    // X Share button
    if (this.xRewardSystem.isRewardAvailable()) {
      const shareBtn = this.createButton(
        centerX,
        height * 0.64,
        "Share on X (+50)",
        0x1da1f2
      );
      shareBtn.on("pointerup", () => {
        this.xRewardSystem.shareAndReward();
        // Update displayed score
        const newState = store.get();
        scoreText.setText(`${newState.score}`);
        // Disable button after use
        shareBtn.disableInteractive();
        shareBtn.setAlpha(0.5);
      });
    }

    // Retry button
    const retryBtn = this.createButton(
      centerX,
      height * 0.76,
      "Retry",
      0x4a90d9
    );
    retryBtn.on("pointerup", () => {
      const highScore = StorageService.loadHighScore();
      store.reset();
      store.set({ highScore });
      this.scene.start("PlayScene");
    });

    // Title button
    const titleBtn = this.createButton(
      centerX,
      height * 0.86,
      "Title",
      0x555555
    );
    titleBtn.on("pointerup", () => {
      this.scene.start("TitleScene");
    });
  }

  private createButton(
    x: number,
    y: number,
    label: string,
    color: number
  ): Phaser.GameObjects.Container {
    const btnWidth = 260;
    const btnHeight = 60;
    const hitPadding = 12;

    const bg = this.add.graphics();
    bg.fillStyle(color, 1);
    bg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 14);

    const text = this.add
      .text(0, 0, label, {
        fontSize: "24px",
        color: "#ffffff",
        fontFamily: "Arial, sans-serif",
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    const container = this.add.container(x, y, [bg, text]);
    const hitW = btnWidth + hitPadding * 2;
    const hitH = btnHeight + hitPadding * 2;
    container.setSize(hitW, hitH);
    container.setInteractive(
      new Phaser.Geom.Rectangle(-hitW / 2, -hitH / 2, hitW, hitH),
      Phaser.Geom.Rectangle.Contains
    );

    // Press feedback
    container.on("pointerdown", () => {
      container.setScale(0.95);
    });
    container.on("pointerup", () => {
      container.setScale(1.0);
    });
    container.on("pointerout", () => {
      container.setScale(1.0);
    });

    return container;
  }
}
